import React from 'react'
import { useForm } from 'react-hook-form'
import { User } from '../services/adminService'
import { formatDateTime } from '../utils/dateUtils'
import { Button } from './ui/Button'
import { useTranslation } from '../i18n/hooks/useTranslation'

interface UserFormProps {
  user: User
  onSubmit: (data: Partial<User>) => void
  onCancel: () => void
  onDelete: (user: User) => void
  isLoading: boolean
}

export function UserForm({ user, onSubmit, onCancel, onDelete, isLoading }: UserFormProps) {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Partial<User>>({
    defaultValues: {
      displayName: user.displayName || '',
      role: user.role || 'user',
      status: user.status || 'active',
      locale: user.locale || 'en',
      settings: user.settings || {
        notifications: true,
        analytics: true,
      },
    },
  })

  // Reset form when user changes
  React.useEffect(() => {
    reset({
      displayName: user.displayName || '',
      role: user.role || 'user',
      status: user.status || 'active',
      locale: user.locale || 'en',
      settings: user.settings || {
        notifications: true,
        analytics: true,
      },
    })
  }, [user, reset])

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          {t('users.editUser')}
        </h2>
      </div>

      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center mb-3">
            <span className="text-2xl mr-2">ℹ️</span>
            <h3 className="font-medium text-blue-900">{t('users.readOnlyInfo')}</h3>
          </div>
          <div className="text-sm text-blue-800 space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div><strong>{t('users.fields.email')}:</strong> {user.email}</div>
              <div><strong>{t('users.fields.userId')}:</strong> {user.id}</div>
              <div><strong>{t('users.fields.created')}:</strong> {formatDateTime(user.createdAt, t)}</div>
              <div><strong>{t('users.fields.lastUpdated')}:</strong> {formatDateTime(user.updatedAt, t)}</div>
              {user.lastLoginAt && (
                <div><strong>{t('users.fields.lastLogin')}:</strong> {formatDateTime(user.lastLoginAt, t)}</div>
              )}
            </div>
            <p className="text-xs text-blue-600 mt-2 italic">{t('users.readOnlyNote')}</p>
          </div>
        </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-2">
            {t('users.fields.displayName')}
          </label>
          <input
            type="text"
            id="displayName"
            {...register('displayName', { required: t('users.validation.displayNameRequired') })}
            className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          {errors.displayName && (
            <p className="text-red-500 text-sm mt-1">{errors.displayName.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
            {t('users.fields.role')}
          </label>
          <select
            id="role"
            {...register('role', { required: t('users.validation.roleRequired') })}
            className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="user">{t('users.roles.user')}</option>
            <option value="moderator">{t('users.roles.moderator')}</option>
            <option value="admin">{t('users.roles.admin')}</option>
          </select>
          {errors.role && (
            <p className="text-red-500 text-sm mt-1">{errors.role.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
            {t('users.fields.status')}
          </label>
          <select
            id="status"
            {...register('status', { required: t('users.validation.statusRequired') })}
            className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="active">{t('common.active')}</option>
            <option value="suspended">{t('common.suspended')}</option>
            <option value="deleted">{t('common.deleted')}</option>
          </select>
          {errors.status && (
            <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="locale" className="block text-sm font-medium text-gray-700 mb-2">
            {t('users.fields.locale')}
          </label>
          <select
            id="locale"
            {...register('locale', { required: t('users.validation.localeRequired') })}
            className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="en">{t('languages.english')}</option>
            <option value="fr">{t('languages.french')}</option>
            <option value="es">{t('languages.spanish')}</option>
            <option value="de">{t('languages.german')}</option>
          </select>
          {errors.locale && (
            <p className="text-red-500 text-sm mt-1">{errors.locale.message}</p>
          )}
        </div>

        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">{t('users.fields.settings')}</label>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="notifications"
              {...register('settings.notifications')}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <label htmlFor="notifications" className="ml-2 text-sm text-gray-700">
              {t('users.settings.enableNotifications')}
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="analytics"
              {...register('settings.analytics')}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <label htmlFor="analytics" className="ml-2 text-sm text-gray-700">
              {t('users.settings.enableAnalytics')}
            </label>
          </div>
        </div>

        <div className="flex justify-between items-center pt-4">
          {/* Delete button */}
          <Button
            type="button"
            variant="danger"
            onClick={() => onDelete(user)}
          >
            {t('users.actions.deleteUser')}
          </Button>
          
          {/* Form actions */}
          <div className="flex space-x-3 ml-auto">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={isLoading}
            >
              {t('common.save')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
} 